import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import LFCButton from '_components/inputs/LFCButton';
import ProgressBar from '_components/ProgressBar';
import {setLFCDataProc} from '_logics/LFCUtil';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import axios, {CancelTokenSource} from 'axios';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import DragHandleIcon from '@mui/icons-material/DragHandle';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  Paper,
  Typography
} from '@mui/material';

export interface ImageItem {
  imageNo: number;
  imagePath: string;
  sortOrder: number;
}

interface SortableImageProps {
  item: ImageItem;
}

const SortableImage = ({item}: SortableImageProps) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: item.imageNo
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <ListItem ref={setNodeRef} style={style} disablePadding sx={{mb: 1}}>
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          width: '100%',
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        <Box
          {...attributes}
          {...listeners}
          sx={{cursor: 'grab', display: 'flex', alignItems: 'center', color: 'text.secondary'}}
        >
          <DragHandleIcon />
        </Box>
        <img
          src={item.imagePath}
          alt={`画像${item.imageNo}`}
          style={{width: 120, height: 68, objectFit: 'fill', borderRadius: 2}}
        />
        <Typography variant="body2" color="text.secondary">
          画像 {item.imageNo}
        </Typography>
      </Paper>
    </ListItem>
  );
};

interface Props {
  open: boolean;
  onClose: (isRefresh: boolean) => void;
  floorId: number;
  floorName: string;
  images: ImageItem[];
}

const FloorImageReorderDialog = (props: Props) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [startProcess, setStartProcess] = useState(false);
  const [items, setItems] = useState<ImageItem[]>([]);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    if (!props.open) {
      setItems([]);
      return;
    }
    const sorted = [...props.images].sort((a, b) => a.sortOrder - b.sortOrder);
    setItems(sorted);
  }, [props.open, props.images]);

  useEffect(() => {
    return () => {
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (!over || active.id === over.id) return;
    setItems(prev => {
      const oldIndex = prev.findIndex(i => i.imageNo === active.id);
      const newIndex = prev.findIndex(i => i.imageNo === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const onSave = () => {
    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      setLFCDataProc({
        snack: enqueueSnackbar,
        name: '並び順更新',
        invoke_name: 'ReorderFloorImages',
        parameters: {
          floorId: props.floorId,
          imageOrders: items.map((item, idx) => ({
            imageNo: item.imageNo,
            sortOrder: idx + 1
          }))
        },
        cancelToken: cancelSource.current.token,
        t
      })
        .then(() => props.onClose(true))
        .catch(err => {
          console.error(err);
          enqueueSnackbar(t('並び順の更新に失敗しました'), {variant: 'error'});
        })
    ]).finally(() => setStartProcess(false));
  };

  return (
    <Dialog open={props.open} onClose={() => props.onClose(false)} maxWidth="xs" fullWidth>
      <ProgressBar startProcess={startProcess} screenLock />
      <LFCDialogTitle onClose={() => props.onClose(false)}>{t('画像並び替え')}</LFCDialogTitle>
      <DialogContent dividers>
        <Box mb={1}>
          <Chip size="small" label={t('フロア名')} />
          <Typography mt={0.5} ml={1}>
            {props.floorName}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" mb={1} display="block">
          {t('ドラッグして並び順を変更してください')}
        </Typography>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map(i => i.imageNo)} strategy={verticalListSortingStrategy}>
            <List disablePadding>
              {items.map(item => (
                <SortableImage key={item.imageNo} item={item} />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      </DialogContent>
      <DialogActions>
        <LFCButton onClick={() => props.onClose(false)}>{t('キャンセル')}</LFCButton>
        <LFCButton color="primary" onClick={onSave}>
          {t('保存')}
        </LFCButton>
      </DialogActions>
    </Dialog>
  );
};

export default FloorImageReorderDialog;
